/**
 * LanguageSwitcher — compact icon-based dropdown for selecting display language (Story 1893).
 *
 * Renders a small globe-icon dropdown intended for placement in the
 * header bar next to the user's display name. Selection is persisted to:
 *   - User account preference (via API call if authenticated)
 *   - localStorage (always, as immediate cache)
 *
 * @module shared/i18n/LanguageSwitcher
 */

import React, { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_STORAGE_KEY,
} from './i18n-config';

interface LanguageSwitcherProps {
  /** Callback to persist language preference to user account (if authenticated). */
  onLanguageChange?: (languageCode: string) => void;
}

/**
 * Compact language dropdown. Displays a globe icon with a small select
 * element for switching the i18next locale. Designed for header placement.
 */
export function LanguageSwitcher({
  onLanguageChange,
}: LanguageSwitcherProps): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const selectId = `ipa-language-select-${useId()}`;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLang = event.target.value;
      void i18n.changeLanguage(newLang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      onLanguageChange?.(newLang);
    },
    [i18n, onLanguageChange],
  );

  return (
    <div
      data-testid="language-switcher"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
      }}
    >
      <label
        htmlFor={selectId}
        style={{ fontSize: '1rem', lineHeight: 1, cursor: 'pointer' }}
        aria-label={t('common:footer.selectLanguage')}
      >
        {'\uD83C\uDF10'}
      </label>
      <select
        id={selectId}
        value={i18n.language}
        onChange={handleChange}
        aria-label={t('common:footer.selectLanguage')}
        data-testid="language-select"
        style={{
          minHeight: '48px',
          minWidth: '48px',
          padding: '0.25rem 1.5rem 0.25rem 0.5rem',
          fontSize: '0.875rem',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\'%3E%3Cpath d=\'M1 1l5 5 5-5\' stroke=\'%236b7280\' stroke-width=\'2\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.4rem center',
          backgroundSize: '0.65rem',
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
}
